import { createRouter, createWebHistory } from "vue-router";

import routes from "./routes";
import Auth from "@/utils/auth";
import { useMetaDataStore } from "@/stores/metaDataStore";
import Constants from "@/utils/constants";
import Helper from "@/utils/helper";
import { usePermissionsStore } from "@/stores/permissionsStore";

const router = createRouter({
  history: createWebHistory(),
  routes
});

// Global navigation guard
router.beforeEach(async (to, from, next) => {
  let isAuthenticated = await Auth.isUserSignedIn();

  if (
    to.path === Constants.enums.ROUTE_ENUM.AUTH_CALLBAK ||
    to.path === Constants.enums.ROUTE_ENUM.AUTH_CALLBAK_MOBILE_APP
  ) {
    next();
  } else if (
    to.path !== Constants.enums.ROUTE_ENUM.LOG_OUT &&
    to.path !== Constants.enums.ROUTE_ENUM.LOG_IN &&
    !isAuthenticated
  ) {
    // Store the redirect path
    const queryParams = new URLSearchParams(to.query);
    queryParams.delete("email");
    const newPath = queryParams.toString()
      ? `${to.path}?${queryParams.toString()}`
      : to.path;
    useMetaDataStore().setData({ redirectPath: newPath });

    // Redirect to sign-in page if not authenticated
    next({
      path: Constants.enums.ROUTE_ENUM.LOG_IN,
      query: to.query
    });
  } else if (to.path === Constants.enums.ROUTE_ENUM.LOG_IN && isAuthenticated) {
    // Redirect to Dashboard if authenticated and navigating to login or sign-in
    next(Constants.enums.ROUTE_ENUM.HOME);
  } else {
    // Enhanced permission-based route guard
    // console.log("to", to);
    if (to.meta?.requiresAuth) {
      const permissionCheck = await checkRouteAccess(to);

      if (!permissionCheck.allowed) {
        console.warn(`Access denied to ${to.path}: ${permissionCheck.reason}`);
        // Redirect to permission denied page with the attempted route and fallback info
        next({
          path: Constants.enums.ROUTE_ENUM.PERMISSION_DENIED,
          query: {
            attemptedRoute: to.fullPath,
            fallbackRoute: permissionCheck.fallbackRoute,
            reason: permissionCheck.reason
          }
        });
        return;
      }
    }

    if (to.fullPath === "/") {
      // Manually redirect to the /home path
      next(Constants.enums.ROUTE_ENUM.HOME);
    } else {
      next();
    }
  }
});

export async function checkRouteAccess(route) {
  const { meta, path, params } = route;

  // Skip permission check if not required
  if (!meta?.requiresAuth) {
    return { allowed: true };
  }

  try {
    const permissionsStore = usePermissionsStore();
    // Get permission type from route meta
    const permissionType = meta.permissionType;
    if (!permissionType) {
      return { allowed: true }; // No permission type specified, allow access
    }

    // Use the appropriate getter based on permission type
    let permissionArray;
    if (permissionType === "dashboard") {
      const combinedPermissions = permissionsStore.getCombinedPermssions(
        Constants.enums.PERMISSION_LEVELS.READER.key
      );
      // Extract IDs from permission objects to match the original string array format
      const dashboardPermissions = combinedPermissions.dashboard || [];
      permissionArray = dashboardPermissions
        .map((item) => item.id)
        .filter(Boolean);
    } else if (permissionType === "form") {
      permissionArray = permissionsStore.getCombinedPermssions(
        Constants.enums.PERMISSION_LEVELS.READER.key
      ).form;
    } else {
      console.error(`Invalid permission type: ${permissionType}`);
      return {
        allowed: false,
        reason: `Invalid permission type: ${permissionType}`,
        fallbackRoute: meta.fallbackRoute || Constants.enums.ROUTE_ENUM.HOME
      };
    }

    if (!permissionArray) {
      return {
        allowed: false,
        reason: `No ${permissionType} permissions`,
        fallbackRoute: meta.fallbackRoute || Constants.enums.ROUTE_ENUM.HOME
      };
    }

    // Super admin check
    if (
      permissionArray.includes(Constants.enums.SUPER_ADMIN_ID.SUPER_ADMIN_ID)
    ) {
      return { allowed: true };
    }

    // Determine the permission ID to check
    let permissionIDToCheck = meta.permissionID;

    // For dynamic routes, resolve the permission ID from the route parameters
    if (!permissionIDToCheck && params.id) {
      permissionIDToCheck = params.id;
    }

    // If no permission ID to check, allow access
    if (!permissionIDToCheck) {
      return { allowed: true };
    }

    // Check specific permission
    const hasPermission = permissionArray.includes(permissionIDToCheck);

    return {
      allowed: hasPermission,
      reason: hasPermission
        ? null
        : `No ${permissionType} permission for: ${permissionIDToCheck}`,
      fallbackRoute: meta.fallbackRoute || Constants.enums.ROUTE_ENUM.HOME
    };
  } catch (error) {
    console.error("Permission check failed:", error);
    return {
      allowed: false,
      reason: "Permission check failed",
      fallbackRoute: meta.fallbackRoute || Constants.enums.ROUTE_ENUM.HOME
    };
  }
}
export default router;
